import { useState } from "react";
import { GuxButton, GuxModal } from "genesys-spark-components-react";
import type { AgentScoringRule } from "@genesys/cloud-frontend/models";
import { useStrings } from "features/i18n/useStrings";
import strings from "./DeleteRuleConfirmation.strings";
import { useAppDispatch } from "app/hooks";
import { deleteAgentScoringRule } from "./agentScoringRulesSlice";
import agentScoringRulesToasts from "./agentScoringRulesToasts";
import { toGuxDisabled } from "util/componentUtil";

interface DeleteRuleConfirmationProps {
    programId: string;
    rule: AgentScoringRule;
    /** Closes the modal (cancel or after delete completes). */
    onClose: () => void;
}

/**
 * Delete confirmation modal for an agent scoring rule.
 * Permission is not re-checked here — the list/table only opens this modal
 * when the user has quality:scoringRule:delete.
 * On success the slice removes the rule from the store; no list refetch needed.
 */
export function DeleteRuleConfirmation({
    programId,
    rule,
    onClose,
}: DeleteRuleConfirmationProps) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const [isDeleting, setIsDeleting] = useState(false);
    const deletingDisabled = toGuxDisabled(isDeleting);

    const handleConfirm = async () => {
        if (!rule.id) {
            onClose();
            return;
        }
        setIsDeleting(true);
        try {
            const action = await dispatch(
                deleteAgentScoringRule({ programId, ruleId: rule.id })
            );
            if (deleteAgentScoringRule.fulfilled.match(action)) {
                agentScoringRulesToasts.deleteSuccess();
            } else {
                agentScoringRulesToasts.deleteFailure();
            }
        } finally {
            setIsDeleting(false);
            onClose();
        }
    };

    return (
        <GuxModal
            open
            size="small"
            onGuxdismiss={onClose}
            data-testid="delete-rule-confirmation"
        >
            <div slot="title" className="gux-heading-lg-bold">
                {t.labels.title}
            </div>
            <div slot="content">{t.labels.message}</div>
            <div slot="start-align-buttons">
                <GuxButton
                    accent="primary"
                    type="button"
                    disabled={deletingDisabled}
                    aria-disabled={deletingDisabled}
                    onClick={handleConfirm}
                    data-testid="delete-rule-confirm"
                >
                    {t.labels.confirm}
                </GuxButton>
            </div>
            <div slot="end-align-buttons">
                <GuxButton
                    accent="secondary"
                    type="button"
                    disabled={deletingDisabled}
                    aria-disabled={deletingDisabled}
                    onClick={onClose}
                    data-testid="delete-rule-cancel"
                >
                    {t.labels.cancel}
                </GuxButton>
            </div>
        </GuxModal>
    );
}

export default DeleteRuleConfirmation;
